import { Link } from 'react-router-dom';
import { Post } from '../../../lib/supabase';

const mockFeatured = [
  {
    slug: 'cursor-ai-workflow-full',
    title: 'CursorとClaudeで仕様書を書いてから実装する流れを整理した',
    excerpt: '「とりあえず作る」から「仕様を固めてから作る」に変えたら、手戻りが激減した。その具体的なフローを記録する。',
    category: '制作フロー',
    categorySlug: 'build-log',
    date: '2026.03.28',
    readTime: '12 min',
  },
  {
    slug: 'vibe-coding-what-is',
    title: 'バイブコーディングとは何か——AIと一緒に作る感覚を言語化してみた',
    excerpt: 'コードを書くというより、AIと会話しながら形にしていく感覚。その実態と、うまくいくときの条件を整理した。',
    category: 'AIツール活用',
    categorySlug: 'ai-creation',
    date: '2026.03.20',
    readTime: '9 min',
  },
  {
    slug: 'ui-reproduction-spotify-log',
    title: 'Spotify風UIをゼロから再現する——詰まったところと解決策の全記録',
    excerpt: 'デザインを見て「これ作れそう」と思ったが、実際には何度も詰まった。その過程をそのまま残す。',
    category: 'UI再現',
    categorySlug: 'ai-creation',
    date: '2026.03.12',
    readTime: '15 min',
  },
];

interface FeaturedArticlesProps {
  posts: Post[];
}

export function FeaturedArticles({ posts }: FeaturedArticlesProps) {
  const articles = posts.length >= 3
    ? posts.slice(0, 3).map((p) => ({
        slug: p.slug,
        title: p.title,
        excerpt: p.excerpt || '',
        category: p.category || '',
        categorySlug: p.category_slug || '',
        date: p.published_at ? new Date(p.published_at).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.') : '',
        readTime: '',
      }))
    : mockFeatured;

  return (
    <section className="py-14 sm:py-20 bg-[#FDFAF6]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">

        {/* Section header */}
        <div className="flex items-baseline justify-between mb-8 sm:mb-10">
          <div>
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-2"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              Start Here
            </p>
            <h2
              className="text-xl sm:text-2xl font-bold text-[#2C2416]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              まず読むならここ
            </h2>
          </div>
          <Link
            to="/articles"
            className="text-xs text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            すべて見る
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Article list */}
        <div className="space-y-0">
          {articles.map((article, index) => (
            <article key={article.slug}>
              <Link
                to={`/articles/${article.slug}`}
                className="block py-6 group cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  {/* Index number */}
                  <span
                    className="text-2xl font-bold text-[#E8E0D5] leading-none mt-1 flex-shrink-0 w-8 text-right"
                    style={{ fontFamily: "'Georgia', serif" }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="text-xs text-[#5B7B6F] font-medium"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {article.category}
                      </span>
                      <span className="text-[#D4C9BE] text-xs">·</span>
                      <span
                        className="text-xs text-[#9A8E84]"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {article.date}
                      </span>
                      {article.readTime && (
                        <>
                          <span className="text-[#D4C9BE] text-xs">·</span>
                          <span
                            className="text-xs text-[#9A8E84]"
                            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                          >
                            {article.readTime}
                          </span>
                        </>
                      )}
                    </div>
                    <h3
                      className="text-base sm:text-lg font-semibold text-[#2C2416] leading-snug mb-2 group-hover:text-[#5B7B6F] transition-colors"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {article.title}
                    </h3>
                    <p
                      className="text-sm text-[#7A6E62] leading-relaxed line-clamp-2"
                      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                    >
                      {article.excerpt}
                    </p>
                  </div>
                </div>
              </Link>
              {index < articles.length - 1 && (
                <div className="border-t border-[#EDE7DF]"></div>
              )}
            </article>
          ))}
        </div>

      </div>
    </section>
  );
}
